import { useToast } from "@chakra-ui/react";
import { isStandstillExemptionRefusal } from "./standstillExemptionRefusal";
import { isEndedContractRefusal } from "./endedContractRefusal";
import { isRejectedTenderRefusal } from "./rejectedTenderRefusal";
import { isUnassessedWinnerRefusal } from "./unassessedWinnerRefusal";
import { isRankingDepartureRefusal } from "./rankingDepartureRefusal";
import { useAppDispatch } from "../../../common/redux/hooks";
import { ceilingRefusalOf } from "./ceilingRefusal";
import { isFlaggedEntriesRefusal } from "../designContest/flaggedEntriesRefusal";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  type CallOffCeilingDto,
  BidStatus,
  Currency,
  SourcingEventStatus,
  StandstillExemption,
  useAwardSourcingEventMutation,
  useGetEvaluationQuery,
  useGetFrameworkAgreementQuery,
  useListBidsForEventQuery,
  type ReceivedBidDto,
  type EvalBidDto,
  type EventValueDto,
  type EvaluationDto,
  type NegotiationRoundDto,
  type SourcingEventDto,
  bffApi,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { JustificationModal } from "../rounds/JustificationModal";
import { awardingDepartsFromPlan } from "../rounds/partIIDialogue";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import { basketBidIds } from "./basketBidIds";
import { eventIsDesignContest } from "../../sourcing-events/buyer/wizard/utils/designContest";
import { useCurrentMoment } from "../../sourcing-events/portal/utils/useCurrentMoment";
import { canSelectWinner } from "./canSelectWinner";
import { hasStandingAwardDecision } from "./hasStandingAwardDecision";
import { seedWinnerIds } from "./seedWinnerIds";
import { juryRankingDeparture, type JuryRankingDeparture } from "./juryRanking";
import { flaggedAmong } from "../designContest/jury";
import { remainingUnderCeiling } from "../../framework-agreement/callOffAvailability";

interface AwardSelection {
  evaluation: EvaluationDto | undefined;
  isLoading: boolean;
  isError: boolean;
  reloadEvaluation: () => void;
  isReloadingEvaluation: boolean;
  winnerIds: string[];
  toggleWinner: (bidId: string) => void;
  awardsOneSupplier: boolean;
  keptOneOfSeveral: boolean;
  internalNote: string;
  setInternalNote: (note: string) => void;
  relativeAdvantages: string;
  setRelativeAdvantages: (text: string) => void;
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  currencyChoices: Currency[];
  amount: string;
  setAmount: (amount: string) => void;
  buyerSetsStandstill: boolean;
  choosesVoluntaryStandstill: boolean;
  standstillDays: string;
  setStandstillDays: (days: string) => void;
  noStandstill: boolean;
  setNoStandstill: (noStandstill: boolean) => void;
  standstillExemption: StandstillExemption | null;
  setStandstillExemption: (exemption: StandstillExemption) => void;
  standstillProblem: StandstillProblem | null;
  minimumStandstillDays: number;
  ruledOutExemptions: ReadonlySet<StandstillExemption>;
  tenderCheck: TenderCheck;
  soleSupplierRefused: boolean;
  recheckTenders: () => void;
  juryRankingDeparture: JuryRankingDeparture | null;
  documentationCheckRequired: boolean;
  documentationCheckOffered: boolean;
  documentationChecked: boolean;
  setDocumentationChecked: (checked: boolean) => void;
  documentationNote: string;
  setDocumentationNote: (note: string) => void;
  award: () => Promise<void>;
  isAwarding: boolean;
  refusedOverFlags: boolean;
  clearRefusedOverFlags: () => void;
  tooManyWinners: boolean;
  endedContractWinners: string[];
  unassessedWinners: UnassessedWinner[];
  ceilingProblem: CeilingProblem | null;
}

export interface UnassessedWinner {
  bidId: string;
  name: string;
}

export type CeilingProblem =
  | { kind: "OVER_CEILING" | "VALUE_REQUIRED"; remaining: number | null }
  | { kind: "TENDER_OVER_CEILING"; remaining: number | null; tenderPrice: EventValueDto }
  | { kind: "TENDER_IN_ANOTHER_CURRENCY"; tenderPrice: EventValueDto; frameworkCurrency: Currency }
  | { kind: "FRAMEWORK_LOADING" }
  | { kind: "FRAMEWORK_UNAVAILABLE"; retry: () => void };

export type TenderCheck = "CHECKED" | "CHECKING" | "FAILED";

export type StandstillProblem =
  | "DAYS_NOT_A_WHOLE_NUMBER_IN_RANGE"
  | "DAYS_BELOW_MINIMUM"
  | "EXEMPTION_MISSING";

export const MAX_STANDSTILL_DAYS = 3650;
export const MAX_VOLUNTARY_STANDSTILL_DAYS = 15;

const DEFAULT_STANDSTILL_DAYS = "10";

const NOTHING_TO_AWARD: AwardSelection = {
  evaluation: undefined,
  isLoading: false,
  isError: false,
  reloadEvaluation: () => undefined,
  isReloadingEvaluation: false,
  winnerIds: [],
  toggleWinner: () => undefined,
  awardsOneSupplier: false,
  keptOneOfSeveral: false,
  internalNote: "",
  setInternalNote: () => undefined,
  relativeAdvantages: "",
  setRelativeAdvantages: () => undefined,
  currency: Currency.Nok,
  setCurrency: () => undefined,
  currencyChoices: Object.values(Currency),
  amount: "",
  setAmount: () => undefined,
  tooManyWinners: false,
  endedContractWinners: [],
  unassessedWinners: [],
  ceilingProblem: null,
  buyerSetsStandstill: false,
  choosesVoluntaryStandstill: false,
  standstillDays: DEFAULT_STANDSTILL_DAYS,
  setStandstillDays: () => undefined,
  noStandstill: false,
  setNoStandstill: () => undefined,
  standstillExemption: null,
  setStandstillExemption: () => undefined,
  standstillProblem: null,
  minimumStandstillDays: 1,
  ruledOutExemptions: new Set(),
  tenderCheck: "CHECKED",
  soleSupplierRefused: false,
  recheckTenders: () => undefined,
  juryRankingDeparture: null,
  documentationCheckRequired: false,
  documentationCheckOffered: false,
  documentationChecked: false,
  setDocumentationChecked: () => undefined,
  documentationNote: "",
  setDocumentationNote: () => undefined,
  award: () => Promise.resolve(),
  refusedOverFlags: false,
  clearRefusedOverFlags: () => undefined,
  isAwarding: false,
};

const AwardSelectionContext = createContext<AwardSelection>(NOTHING_TO_AWARD);

interface AwardForm {
  eventId: string;
  /** null while the seeded winners still stand; an array once the buyer has touched the ticks. */
  picked: string[] | null;
  internalNote: string;
  relativeAdvantages: string;
  currency: Currency;
  amount: string;
  standstillDays: string;
  noStandstill: boolean;
  standstillExemption: StandstillExemption | null;
  soleSupplierRefusedFor: string | null;
  documentationCheckedFor: string | null;
  documentationNote: string;
}

const emptyForm = (event: SourcingEventDto): AwardForm => {
  const estimated =
    event.eventContext.type === "Public" || event.eventContext.type === "MiniCompetition"
      ? event.eventContext.estimatedValue
      : null;
  return {
    eventId: event.id,
    picked: null,
    internalNote: "",
    relativeAdvantages: "",
    currency: estimated?.currency ?? Currency.Nok,
    amount:
      estimated?.value != null && !eventIsDesignContest(event)
        ? String(estimated.value)
        : "",
    standstillDays: event.choosesVoluntaryStandstill
      ? "0"
      : DEFAULT_STANDSTILL_DAYS,
    noStandstill: false,
    standstillExemption: null,
    soleSupplierRefusedFor: null,
    documentationCheckedFor: null,
    documentationNote: "",
  };
};

// FOA § 25-2 (2) a / forsyningsforskriften § 21-2 (2) a: none is required, so 0 is the default.
const voluntaryStandstillProblemOf = (
  form: AwardForm,
): StandstillProblem | null => {
  const days = form.standstillDays.trim();
  const whole = /^\d+$/.test(days) ? Number(days) : NaN;
  return whole >= 0 && whole <= MAX_VOLUNTARY_STANDSTILL_DAYS
    ? null
    : "DAYS_NOT_A_WHOLE_NUMBER_IN_RANGE";
};

const standstillProblemOf = (
  form: AwardForm,
  ruledOut: ReadonlySet<StandstillExemption>,
  minimumDays: number,
): StandstillProblem | null => {
  if (form.noStandstill) {
    return form.standstillExemption == null ||
      ruledOut.has(form.standstillExemption)
      ? "EXEMPTION_MISSING"
      : null;
  }
  const days = form.standstillDays.trim();
  const whole = /^\d+$/.test(days) ? Number(days) : NaN;
  if (!(whole >= 1 && whole <= MAX_STANDSTILL_DAYS))
    return "DAYS_NOT_A_WHOLE_NUMBER_IN_RANGE";
  return whole < minimumDays ? "DAYS_BELOW_MINIMUM" : null;
};

// FOA § 10-2 (3): b only when no other supplier tendered, c only for a call-off under a framework agreement.
const exemptionsRuledOut = (
  deliveredBids: readonly ReceivedBidDto[] | undefined,
  winnerIds: string[],
  soleSupplierRefused: boolean,
): ReadonlySet<StandstillExemption> => {
  const othersTendered =
    deliveredBids === undefined ||
    othersDeliveredAnyTender(deliveredBids, winnerIds);
  const moreThanOneWinner = winnerIds.length > 1;
  return new Set([
    ...(othersTendered || moreThanOneWinner || soleSupplierRefused
      ? [StandstillExemption.SoleConcernedSupplier]
      : []),
    StandstillExemption.FrameworkAgreementCallOff,
  ]);
};

// Mirrors SourcingEventService.soleConcernedSupplier: every round counts, not only the one being awarded.
const othersDeliveredAnyTender = (
  deliveredBids: readonly ReceivedBidDto[],
  winnerIds: string[],
): boolean => {
  const delivered = deliveredBids.filter(
    (bid) => bid.status !== BidStatus.Draft,
  );
  const winners = new Set(
    delivered
      .filter((bid) => winnerIds.includes(bid.id))
      .map((bid) => bid.owner),
  );
  return delivered.some((bid) => !winners.has(bid.owner));
};

const standstillRequest = (form: AwardForm) =>
  form.noStandstill
    ? { standstillExemption: form.standstillExemption }
    : { standstillDays: Number(form.standstillDays.trim()) };

const collectsAward = (
  event: SourcingEventDto,
  rounds: readonly NegotiationRoundDto[],
  now: number,
): boolean =>
  (event.status === SourcingEventStatus.Evaluation ||
    event.status === SourcingEventStatus.Evaluated) &&
  canSelectWinner(event, rounds, now) &&
  !hasStandingAwardDecision(event);

const REJECTED_TENDER_TOAST = "award-rejected-tender";

export const AwardSelectionProvider = ({
  event,
  children,
}: {
  event: SourcingEventDto;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { trigger } = useApiError();
  const dispatch = useAppDispatch();
  const [refusedOverFlags, setRefusedOverFlags] = useState(false);
  const [rejectedTenderRefusal, setRejectedTenderRefusal] = useState<{
    chosen: EvalBidDto[];
    refusedAt: number;
  } | null>(null);
  const { rounds, latestSentRound } = useRoundSelection();
  const now = useCurrentMoment().valueOf();
  const {
    currentData: evaluation,
    isLoading: isFirstLoad,
    isError,
    isFetching: isReloadingEvaluation,
    fulfilledTimeStamp: evaluationFetchedAt,
    refetch: reloadEvaluation,
  } = useGetEvaluationQuery(
    collectsAward(event, rounds, now)
      ? { eventId: event.id, round: latestSentRound?.id }
      : skipToken,
  );
  const isLoading =
    isFirstLoad || (evaluation === undefined && isReloadingEvaluation);
  const {
    data: deliveredBidsData,
    isError: tenderCheckFailed,
    refetch: recheckTenders,
  } = useListBidsForEventQuery(
    collectsAward(event, rounds, now) ? { eventId: event.id } : skipToken,
  );
  const [awardEvent, { isLoading: isAwarding }] =
    useAwardSourcingEventMutation();

  // React Router reuses SourcingEventView across a :eventId change, so the form has to notice.
  const [form, setForm] = useState(() => emptyForm(event));
  if (form.eventId !== event.id) setForm(emptyForm(event));
  const { picked, internalNote, amount } = form;
  const callOffFramework = useCallOffFramework(event);
  const remainingUnderTheCeiling = callOffFramework.framework ? remainingUnderCeiling(callOffFramework.framework) : null;
  const frameworkCurrency = callOffFramework.framework
    ? (callOffFramework.framework.ceilingValue?.currency ?? Currency.Nok)
    : callOffFramework.undisclosed
      ? (callOffFramework.undisclosed.ceiling?.currency ?? Currency.Nok)
      : null;
  const currency = frameworkCurrency ?? form.currency;
  const buyerSetsStandstill = event.buyerSetsStandstill ?? false;
  const choosesVoluntaryStandstill = event.choosesVoluntaryStandstill ?? false;

  const awardable = new Set(
    (evaluation?.bids ?? [])
      .filter((bid) => bid.status !== BidStatus.Rejected)
      .map((bid) => bid.bidId),
  );
  const seeded = evaluation
    ? seedWinnerIds(evaluation, basketBidIds(event, latestSentRound))
    : [];
  const awardsOneSupplier = event.eventContext.type === "MiniCompetition";
  const eligible = (picked ?? seeded).filter((id) => awardable.has(id));
  const keptOneOfSeveral = awardsOneSupplier && eligible.length > 1;
  const winnerIds = keptOneOfSeveral ? [bestRanked(eligible, evaluation?.bids ?? [])] : eligible;
  const winnersKey = [...winnerIds].sort().join(",");
  const tenderPrice = winnersPrice(winnerIds, evaluation?.bids ?? []);
  const ceilingProblem: CeilingProblem | null = !callOffFramework.applies
    ? ceilingProblemOf(remainingUnderTheCeiling, amount, tenderPrice)
    : callOffFramework.isError
      ? { kind: "FRAMEWORK_UNAVAILABLE", retry: callOffFramework.refetch }
      : !frameworkCurrency
        ? { kind: "FRAMEWORK_LOADING" }
        : tenderPrice?.value != null && tenderPrice.currency !== frameworkCurrency
          ? { kind: "TENDER_IN_ANOTHER_CURRENCY", tenderPrice, frameworkCurrency }
          : callOffFramework.undisclosed
            ? undisclosedCeilingProblemOf(callOffFramework.undisclosed.ceiling, winnerIds, amount, tenderPrice)
            : ceilingProblemOf(remainingUnderTheCeiling, amount, tenderPrice);
  const soleSupplierRefused = form.soleSupplierRefusedFor === winnersKey;
  const documentationCheckOffered = event.winnerDocumentation?.recordBeforeAward ?? false;
  const documentationCheckRequired = documentationCheckOffered;
  const documentationChecked = form.documentationCheckedFor === winnersKey;
  const checkedBids = tenderCheckFailed ? undefined : deliveredBidsData?.bids;
  const ruledOutExemptions = exemptionsRuledOut(
    checkedBids,
    winnerIds,
    soleSupplierRefused,
  );
  const tenderCheck: TenderCheck = tenderCheckFailed
    ? "FAILED"
    : deliveredBidsData
      ? "CHECKED"
      : "CHECKING";
  const minimumStandstillDays = event.minimumStandstillDays ?? 1;
  const standstillProblem = choosesVoluntaryStandstill
    ? voluntaryStandstillProblemOf(form)
    : buyerSetsStandstill
      ? standstillProblemOf(form, ruledOutExemptions, minimumStandstillDays)
      : null;

  const change = (fields: Partial<AwardForm>) =>
    setForm((previous) => ({ ...previous, ...fields }));

  const toggleWinner = (bidId: string) =>
    change({
      picked: winnerIds.includes(bidId)
        ? winnerIds.filter((id) => id !== bidId)
        : awardsOneSupplier
          ? [bidId]
          : [...winnerIds, bidId],
    });

  const [asksForDepartureJustification, setAsksForDepartureJustification] =
    useState(false);
  const [asksForJuryDepartureReason, setAsksForJuryDepartureReason] =
    useState(false);
  const [asksForRankingDepartureReason, setAsksForRankingDepartureReason] =
    useState(false);
  const rejectedIds = new Set(
    (evaluation?.bids ?? [])
      .filter((bid) => bid.status === BidStatus.Rejected)
      .map((bid) => bid.bidId),
  );
  const departureFromJury = juryRankingDeparture(event, winnerIds, rejectedIds);
  const departsFromBestRanked =
    awardsOneSupplier && departsFromBestRankedTender(winnerIds, evaluation?.bids ?? []);

  const send = async ({
    dialogueDepartureJustification,
    juryDepartureJustification,
    rankingDepartureJustification,
  }: {
    dialogueDepartureJustification?: string;
    juryDepartureJustification?: string;
    rankingDepartureJustification?: string;
  } = {}) => {
    if (winnerIds.length === 0) return;
    const parsed = amount.trim() === "" ? null : Number(amount);
    try {
      await awardEvent({
        eventId: event.id,
        awardSourcingEventRequest: {
          winningBidIds: winnerIds,
          internalNote: internalNote.trim() || null,
          relativeAdvantages: form.relativeAdvantages.trim() || null,
          eventValue:
            parsed != null && !Number.isNaN(parsed)
              ? { value: parsed, currency }
              : null,
          dialogueDepartureJustification,
          juryDepartureJustification,
          rankingDepartureJustification,
          ...(flaggedAmong(
            event.eventContext.type === "Public"
              ? event.eventContext.designContest
              : null,
            winnerIds,
          ).length > 0
            ? { proceedDespiteFlaggedEntries: true }
            : {}),
          ...(buyerSetsStandstill ? standstillRequest(form) : {}),
          ...(choosesVoluntaryStandstill
            ? { standstillDays: Number(form.standstillDays.trim()) }
            : {}),
          ...(documentationCheckOffered && documentationChecked
            ? { winnerDocumentationCheck: { note: form.documentationNote.trim() || null } }
            : {}),
        },
      }).unwrap();
      setAsksForDepartureJustification(false);
      setAsksForJuryDepartureReason(false);
      setAsksForRankingDepartureReason(false);
      toast({
        title: t("Event awarded"),
        status: "success",
        position: "top-right",
      });
    } catch (e) {
      if (isFlaggedEntriesRefusal(e)) {
        setAsksForDepartureJustification(false);
        setAsksForJuryDepartureReason(false);
        setAsksForRankingDepartureReason(false);
        setRefusedOverFlags(true);
        dispatch(bffApi.util.invalidateTags(["SourcingEvent"]));
        return;
      }
      if (isRankingDepartureRefusal(e)) {
        setAsksForDepartureJustification(false);
        setAsksForJuryDepartureReason(false);
        reloadEvaluation();
        setAsksForRankingDepartureReason(true);
        return;
      }
      if (isEndedContractRefusal(e)) {
        setAsksForDepartureJustification(false);
        setAsksForJuryDepartureReason(false);
        setAsksForRankingDepartureReason(false);
        dispatch(bffApi.util.invalidateTags(["SourcingEvent"]));
        return;
      }
      if (isUnassessedWinnerRefusal(e)) {
        setAsksForDepartureJustification(false);
        setAsksForJuryDepartureReason(false);
        setAsksForRankingDepartureReason(false);
        reloadEvaluation();
        return;
      }
      if (isRejectedTenderRefusal(e)) {
        setAsksForDepartureJustification(false);
        setAsksForJuryDepartureReason(false);
        setAsksForRankingDepartureReason(false);
        sayWhichTenderWasRejected();
        return;
      }
      if (ceilingRefusalOf(e) && !callOffFramework.undisclosed) void callOffFramework.refetch();
      if (isStandstillExemptionRefusal(e)) {
        if (
          form.standstillExemption === StandstillExemption.SoleConcernedSupplier
        ) {
          change({ soleSupplierRefusedFor: winnersKey });
        }
        recheckTenders();
      }
      trigger(e);
    }
  };

  const sayWhichTenderWasRejected = () => {
    setRejectedTenderRefusal({
      chosen: (evaluation?.bids ?? []).filter((bid) =>
        winnerIds.includes(bid.bidId),
      ),
      refusedAt: Date.now(),
    });
    reloadEvaluation();
  };

  useEffect(() => {
    if (!rejectedTenderRefusal || isReloadingEvaluation) return;
    const reloaded =
      evaluation !== undefined &&
      (evaluationFetchedAt ?? 0) >= rejectedTenderRefusal.refusedAt;
    if (!reloaded && !isError) return;
    setRejectedTenderRefusal(null);
    const { chosen } = rejectedTenderRefusal;
    const stillAwardable = new Set(
      (reloaded ? evaluation.bids : [])
        .filter((bid) => bid.status !== BidStatus.Rejected)
        .map((bid) => bid.bidId),
    );
    const rejectedNames = reloaded
      ? chosen
          .filter((bid) => !stillAwardable.has(bid.bidId))
          .map((bid) => bid.supplierName)
      : [];
    if (reloaded)
      change({
        picked: chosen
          .map((bid) => bid.bidId)
          .filter((id) => stillAwardable.has(id)),
      });
    if (toast.isActive(REJECTED_TENDER_TOAST)) return;
    toast({
      id: REJECTED_TENDER_TOAST,
      status: "error",
      position: "top-right",
      title:
        rejectedNames.length > 0
          ? t(
              "The tender from {{suppliers}} has been rejected and can't be awarded. Choose another tenderer.",
              {
                suppliers: rejectedNames.join(", "),
              },
            )
          : t(
              "A tender you chose has been rejected and can't be awarded. Choose another tenderer.",
            ),
    });
  }, [
    rejectedTenderRefusal,
    isReloadingEvaluation,
    evaluation,
    evaluationFetchedAt,
    isError,
    toast,
    t,
  ]);

  const award = async () => {
    if (winnerIds.length === 0 || standstillProblem != null) return;
    if (documentationCheckRequired && !documentationChecked) return;
    if (unassessedWinnersOf(winnerIds, evaluation?.bids ?? []).length > 0) return;
    if (departureFromJury === "REFUSED_BY_BINDING_JURY") return;
    if (departureFromJury === "NEEDS_REASON") {
      setAsksForJuryDepartureReason(true);
      return;
    }
    if (departsFromBestRanked) {
      setAsksForRankingDepartureReason(true);
      return;
    }
    if (awardingDepartsFromPlan(event, rounds)) {
      setAsksForDepartureJustification(true);
      return;
    }
    await send();
  };

  return (
    <AwardSelectionContext.Provider
      value={{
        evaluation,
        isLoading,
        isError,
        reloadEvaluation,
        isReloadingEvaluation,
        winnerIds,
        toggleWinner,
        awardsOneSupplier,
        keptOneOfSeveral,
        internalNote,
        setInternalNote: (note) => change({ internalNote: note }),
        relativeAdvantages: form.relativeAdvantages,
        setRelativeAdvantages: (text) => change({ relativeAdvantages: text }),
        currency,
        setCurrency: (next) => change({ currency: next }),
        currencyChoices: !callOffFramework.applies
          ? Object.values(Currency)
          : frameworkCurrency
            ? [frameworkCurrency]
            : [],
        amount,
        setAmount: (next) => change({ amount: next }),
        buyerSetsStandstill,
        choosesVoluntaryStandstill,
        standstillDays: form.standstillDays,
        setStandstillDays: (days) => change({ standstillDays: days }),
        noStandstill: choosesVoluntaryStandstill
          ? Number(form.standstillDays.trim()) === 0
          : form.noStandstill,
        setNoStandstill: (next) => change({ noStandstill: next }),
        standstillExemption: form.standstillExemption,
        setStandstillExemption: (exemption) =>
          change({ standstillExemption: exemption }),
        standstillProblem,
        minimumStandstillDays,
        ruledOutExemptions,
        tenderCheck,
        soleSupplierRefused:
          soleSupplierRefused &&
          checkedBids !== undefined &&
          !othersDeliveredAnyTender(checkedBids, winnerIds),
        recheckTenders: () => void recheckTenders(),
        juryRankingDeparture: departureFromJury,
        documentationCheckRequired,
        documentationCheckOffered,
        documentationChecked,
        setDocumentationChecked: (checked) =>
          change({ documentationCheckedFor: checked ? winnersKey : null }),
        documentationNote: form.documentationNote,
        setDocumentationNote: (note) => change({ documentationNote: note }),
        award,
        refusedOverFlags,
        clearRefusedOverFlags: () => setRefusedOverFlags(false),
        isAwarding,
        tooManyWinners: exceedsFrameworkParticipants(event, winnerIds.length),
        endedContractWinners: endedContractWinnersOf(event, winnerIds, evaluation?.bids ?? []),
        unassessedWinners: unassessedWinnersOf(winnerIds, evaluation?.bids ?? []),
        ceilingProblem,
      }}
    >
      {children}
      <JustificationModal
        isOpen={asksForDepartureJustification}
        title={t("Award without the planned dialogue")}
        description={t(
          "The procurement documents said dialogue was planned. Explain why you award without it (FOA § 10-5 (2) h).",
        )}
        label={t("Why the award is made without dialogue")}
        confirmLabel={t("Award without dialogue")}
        isLoading={isAwarding}
        onConfirm={(justification) =>
          void send({ dialogueDepartureJustification: justification })
        }
        onClose={() => setAsksForDepartureJustification(false)}
      />
      <JustificationModal
        isOpen={asksForJuryDepartureReason}
        title={t("Choose winners other than the jury's ranking")}
        description={t(
          "The jury only gives an opinion, so you may depart from its ranking. Explain why. The reason goes into the protocol.",
        )}
        label={t("Why you depart from the jury's ranking")}
        confirmLabel={t("Choose these winners")}
        isLoading={isAwarding}
        onConfirm={(justification) =>
          void send({ juryDepartureJustification: justification })
        }
        onClose={() => setAsksForJuryDepartureReason(false)}
      />
      <JustificationModal
        isOpen={asksForRankingDepartureReason}
        title={t("Award to a tender that isn't ranked first")}
        description={t(
          "In a mini-competition the call-off goes to the best tender on the framework agreement's award criteria. Explain why you award to this one. The reason is recorded with the award.",
        )}
        label={t("Why you award to this tender")}
        confirmLabel={t("Award to this tender")}
        isLoading={isAwarding}
        onConfirm={(justification) =>
          void send({ rankingDepartureJustification: justification })
        }
        onClose={() => setAsksForRankingDepartureReason(false)}
      />
    </AwardSelectionContext.Provider>
  );
};

export const unassessedWinnersOf = (winnerIds: string[], bids: EvalBidDto[]): UnassessedWinner[] =>
  bids
    .filter((bid) => winnerIds.includes(bid.bidId) && bid.awaitsExclusionGroundsAssessment)
    .map((bid) => ({ bidId: bid.bidId, name: bid.supplierName }));

export const endedContractWinnersOf = (
  event: SourcingEventDto,
  winnerIds: string[],
  bids: EvalBidDto[],
): string[] => {
  const ended = event.frameworkAgreementLink?.tendersFromEndedContracts ?? [];
  return bids.filter((bid) => winnerIds.includes(bid.bidId) && ended.includes(bid.bidId)).map((bid) => bid.supplierName);
};

const exceedsFrameworkParticipants = (event: SourcingEventDto, winnerCount: number): boolean => {
  if (event.eventContext.type !== "Public" || event.eventContext.frameworkAgreement == null) return false;
  const max = event.eventContext.frameworkMaxParticipants;
  return max != null && winnerCount > max;
};

const useCallOffFramework = (event: SourcingEventDto) => {
  const frameworkAgreementId =
    event.eventContext.type === "MiniCompetition" ? event.eventContext.frameworkAgreementId : null;
  const link = event.frameworkAgreementLink;
  const undisclosed = frameworkAgreementId !== null && link?.mayOpen === false ? { ceiling: link.ceiling ?? null } : null;
  const { data: framework, isError, refetch } = useGetFrameworkAgreementQuery(
    frameworkAgreementId && !undisclosed ? { frameworkAgreementId } : skipToken,
    { refetchOnFocus: true, refetchOnMountOrArgChange: true },
  );
  return {
    applies: frameworkAgreementId !== null,
    framework,
    undisclosed,
    isError: !framework && !undisclosed && isError,
    refetch,
  };
};

export const cannotBeAwardedUnderTheCeiling = (problem: CeilingProblem): boolean =>
  problem.kind === "TENDER_OVER_CEILING" || (problem.kind === "OVER_CEILING" && problem.remaining === null);

export const undisclosedCeilingProblemOf = (
  ceiling: CallOffCeilingDto | null,
  winnerIds: string[],
  amount: string,
  tenderPrice: EventValueDto | null,
): CeilingProblem | null => {
  if (!ceiling) return null;
  if (tenderPrice?.value != null) {
    return winnerIds.some((id) => ceiling.tendersOverCeiling.includes(id))
      ? { kind: "TENDER_OVER_CEILING", remaining: null, tenderPrice }
      : null;
  }
  if (!ceiling.hasRoom) return { kind: "OVER_CEILING", remaining: null };
  return amount.trim() === "" ? { kind: "VALUE_REQUIRED", remaining: null } : null;
};

const winnersPrice = (winnerIds: string[], bids: EvalBidDto[]): EventValueDto | null => {
  const prices: { value: number; currency: EventValueDto["currency"] }[] = [];
  for (const id of winnerIds) {
    const price = bids.find((bid) => bid.bidId === id)?.effectivePrice;
    if (price?.value == null) return null;
    prices.push({ value: price.value, currency: price.currency });
  }
  const [first, ...rest] = prices;
  if (!first || rest.some((price) => price.currency !== first.currency)) return null;
  return { value: prices.reduce((total, price) => total + price.value, 0), currency: first.currency };
};

export const ceilingProblemOf = (
  remaining: number | null,
  amount: string,
  tenderPrice: EventValueDto | null,
): CeilingProblem | null => {
  if (remaining === null) return null;
  if (tenderPrice?.value != null) {
    return tenderPrice.value > remaining ? { kind: "TENDER_OVER_CEILING", remaining, tenderPrice } : null;
  }
  const awardedValue = amount.trim() === "" ? null : Number(amount);
  if (remaining <= 0 || (awardedValue !== null && awardedValue > remaining)) {
    return { kind: "OVER_CEILING", remaining };
  }
  return awardedValue === null ? { kind: "VALUE_REQUIRED", remaining } : null;
};

export const useAwardSelection = (): AwardSelection =>
  useContext(AwardSelectionContext);

export const departsFromBestRankedTender = (winnerIds: string[], bids: EvalBidDto[]): boolean => {
  const ranks = bids.filter((bid) => bid.status !== BidStatus.Rejected && bid.rank > 0).map((bid) => bid.rank);
  if (ranks.length === 0) return false;
  const best = Math.min(...ranks);
  return winnerIds.some((id) => {
    const rank = bids.find((bid) => bid.bidId === id)?.rank ?? 0;
    return rank <= 0 || rank > best;
  });
};

const bestRanked = (bidIds: string[], bids: EvalBidDto[]): string => {
  const rankOf = (bidId: string) => {
    const rank = bids.find((bid) => bid.bidId === bidId)?.rank ?? 0;
    return rank > 0 ? rank : Number.POSITIVE_INFINITY;
  };
  return [...bidIds].sort((a, b) => rankOf(a) - rankOf(b))[0];
};
